// Help > Glossary: every term in the screen's language, with the other
// language's name underneath (reports and datasheets often use the English
// one). Searchable in both languages, accent-insensitive, and filterable by
// topic. The texts live in i18n/glossary.js and are never machine-translated,
// hence data-no-translate.
import { useMemo, useState } from "react";
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, Stack, TextField, Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useLanguage } from "../context/LanguageContext";
import { GLOSSARY, GLOSSARY_GROUPS, filterGlossary } from "../i18n/glossary";

export default function GlossaryDialog({ open, onClose }) {
  const { t, isGreek } = useLanguage();
  const lang = isGreek ? "el" : "en";
  const other = isGreek ? "en" : "el";
  const [query, setQuery] = useState("");
  const [group, setGroup] = useState("all");

  const items = useMemo(() => filterGlossary(GLOSSARY, { query, group, lang }), [query, group, lang]);
  const counts = useMemo(() => {
    const byGroup = Object.fromEntries(GLOSSARY_GROUPS.map((g) => [g, 0]));
    filterGlossary(GLOSSARY, { query, lang }).forEach((item) => { byGroup[item.group] += 1; });
    return byGroup;
  }, [query, lang]);
  const total = GLOSSARY_GROUPS.reduce((sum, g) => sum + counts[g], 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth data-no-translate="">
      <DialogTitle sx={{ pb: 1 }}>{t("help.glossary")}</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <TextField
          fullWidth
          size="small"
          autoFocus
          label={t("glossary.search")}
          placeholder={t("glossary.placeholder")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ mt: 1, mb: 1.5 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }} role="group" aria-label={t("glossary.topics")}>
          {["all", ...GLOSSARY_GROUPS].map((g) => (
            <Chip
              key={g}
              label={`${t(`glossary.group.${g}`)} (${g === "all" ? total : counts[g]})`}
              color={group === g ? "primary" : "default"}
              variant={group === g ? "filled" : "outlined"}
              onClick={() => setGroup(g)}
              aria-pressed={group === g}
            />
          ))}
        </Stack>

        {items.length ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            {items.map((item) => (
              <Box
                key={item.term.en}
                data-testid="glossary-item"
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  border: (theme) => `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.08)"}`,
                  background: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)"),
                }}
              >
                <Stack direction="row" spacing={1} alignItems="baseline" justifyContent="space-between">
                  <Typography sx={{ fontWeight: 800 }}>{item.term[lang]}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                    {t(`glossary.group.${item.group}`)}
                  </Typography>
                </Stack>
                {item.term[other] !== item.term[lang] ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontStyle: "italic" }}>
                    {item.term[other]}
                  </Typography>
                ) : null}
                <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>{item.desc[lang]}</Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography color="text.secondary" sx={{ py: 2 }}>{t("glossary.empty")}</Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between", px: 3 }}>
        <Typography variant="caption" color="text.secondary">{t("glossary.manualsHint")}</Typography>
        <Button onClick={onClose}>{t("glossary.close")}</Button>
      </DialogActions>
    </Dialog>
  );
}
